











































































































/* eslint-disable max-len */
import {
  defineComponent, reactive, toRefs, onBeforeMount, computed, ComputedRef,
} from '@vue/composition-api'
import Pod from '@/components/layouts/Pod.vue'
import PodCard from '@/components/layouts/PodCard.vue'
import ApiError from '@/components/appBase/errors/ApiError.vue'
import EditableTabularList from '@/components/appBase/forms/EditableTabularList.vue'
import DelayedDisplay from '@/components/appBase/DelayedDisplay.vue'
import useApiErrorViewHandler from '@/components/appBase/errors/useApiErrorViewHandler'
import useClientflowApi from '@/api/clientFlow/useClientflowApi'
import { getHumanDateFromUnixTimestamp } from '@/lib/utils/getHumanDateFromUnixTimeStamp'
import { TextField, SwitchField } from '@/components/types/FormFields'

// const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

type RequireAtLeastOne<T, Keys extends keyof T = keyof T> =
    Pick<T, Exclude<keyof T, Keys>>
    & {
        [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>
    }[Keys]

const getDanishPhoneNumberRegExp = () => RegExp(/\+?\b(?:45\s|45\s|45|)(?:\d{2}\s?){3}\d{2}\b/g)
const isPhoneNumber = (val: string, regExp: RegExp) => regExp.test(val.trim())
const validationRules = {
  email: [
    (v: string) => !!v || 'E-mail er obligatorisk',
    (v: string) => /.+@.+\..+/.test(v) || 'E-mail adressen skal være gyldig',
  ],
  name: [
    (v: string) => !!v || 'Navn er obligatorisk',
    (v: string) => (v && v.length >= 2) || 'Navnet skal bestå af mindst to bogstaver',
  ],
  phone: [(v: string | undefined) => {
    if (typeof v !== 'string') return true
    return (v.trim() === ''
    || isPhoneNumber(v, getDanishPhoneNumberRegExp())
    || 'Telefonnr skal være et gyldigt dansk tlfnr')
  },
  ],
}

/**
 * Shape of the data, that we get from vuex store.
 * TODO: put this definition somewhere in the store.
 */
interface RemoteClientData {
  name: string;
  email: string;
  phone: string;
  isDummy: boolean;
  id: string;
  dbCreatedAt: {seconds: number};
}

/**
 * Shape of the update object that is sent back to the
 * api. Define the fields manually here.
 */
type ClientUpdateData = RequireAtLeastOne<RemoteClientData, 'name' | 'email' | 'phone' | 'isDummy' >

/**
 * Shape of the particular fields that we display in this component.
 * It is a mix of proper form fields and non-editable info (e.g. created).
 */
interface ClientFields {
  name: TextField;
  email: TextField;
  phone: TextField;
  isDummy: SwitchField;
  created: {label: string; value?: string};
}

/**
 * Define the fixed properties of fields, constrained to ClientFields.
 * The 'value' prop for each field will be added later in the code.
 */
const clientFields: ClientFields = {
  name: {
    label: 'Navn',
    type: 'text',
    placeholder: 'Klientens navn',
    validationFns: validationRules.name,
    editable: true,
  },
  email: {
    label: 'Email',
    type: 'text',
    validationFns: validationRules.email,
    editable: true,
  },
  phone: {
    label: 'Telefon',
    type: 'text',
    validationFns: validationRules.phone,
    editable: true,
  },
  created: {
    label: 'Oprettet',
  },
  isDummy: {
    label: 'Test-klient',
    type: 'switch',
    editable: true,
  },

}

type State = {
  loading: boolean;
  editToggle: boolean;
}

const initialState: State = {
  loading: true,
  editToggle: false,
}

export default defineComponent({
  components: {
    ApiError, DelayedDisplay, Pod, PodCard, EditableTabularList,
  },
  props: {
    clientId: { type: String, default: '' },
  },

  setup(props, context) {
    // Set up state
    const state = reactive({ ...initialState })

    /** reactive get client from store */
    const client: ComputedRef<RemoteClientData> = computed(() => context.root.$store.getters['clients/getById'](props.clientId))

    /**
     * Returns the client fields with the 'value' property added. Updates when
     * there is a change in client (i.e. a change in remote data on the client)
     */
    const myClientFields: ComputedRef<ClientFields | undefined> = computed(() => {
      if (client.value === undefined) return undefined
      const localizedDate = getHumanDateFromUnixTimestamp(
        client.value.dbCreatedAt.seconds,
        context.root.$vuetify.lang.current,
        { year: 'numeric', month: 'short', day: 'numeric' },
      )

      return {
        name: { ...clientFields.name, value: client.value.name },
        email: { ...clientFields.email, value: client.value.email },
        phone: { ...clientFields.phone, value: client.value.phone },
        created: { ...clientFields.created, value: localizedDate },
        isDummy: {
          ...clientFields.isDummy,
          value: client.value.isDummy,
          valueText: client.value.isDummy === false ? 'Nej' : 'Ja',
        },
      }
    })

    // Compose!
    // ...delegate api handling to useClientflowApi():
    const {
      ClientFlowApi,
      awaitingResponse,
      hasResponseError,
      handler,
      reset,
      errorDisplay,
    } = useClientflowApi()

    // ...delegate logic around showing api error to useApiErrorViewHandler():
    const {
      errorVisibilityHasChangedTo,
      toggleShowApiError,
      showResponseError,
      showingResponseError,
    } = useApiErrorViewHandler(hasResponseError)

    /**
     * The function that calls the api
     */
    async function updateClientInfo(clientId: string, data: ClientUpdateData) {
      const cmd = await ClientFlowApi.updateClientContactInfo({ ...data, clientId })
      const response = await handler.handle(cmd)
      if (response.isOk()) {
        // state.isInEditMode = false
      }
    }

    /**
     * Resets the state of the api handler (e.g. removes error state, etc)
     */
    function onFormClosed() {
      reset()
    }

    /**
     * The update function that is exposed to the template
     */
    async function onUpdateClientFields(data: ClientUpdateData) {
      await updateClientInfo(client.value.id, data)
    }

    /**
     * Wire the component to our vuex implementation, e.g. get the client
     * If the client is undefined, open a new channel (this is specific to
     * the vuex/firebase lib that we are using)
     * TODO: implement error handling, i.e. no access to firebase.
     */
    onBeforeMount(() => {
      try {
        if (context.root.$store.getters['clients/getById'](props.clientId) === undefined) {
          // console.log('findes ikke', props.clientId)
          state.loading = true
          // https://stackoverflow.com/questions/47876754/query-firestore-database-for-document-id
          context.root.$store.dispatch('clients/openDBChannel', {
            where: [['__name__', '==', props.clientId]],
          })
            .then(() => {
              state.loading = false
              // console.log('we got data')
            })
        } else {
          state.loading = false
          // console.log('we already had the data')
        }
        // setFieldsToUpdateOnRemoteChange(true)
      } catch (e) {
        state.loading = false
        // console.log('some error')
        // console.log(e)
        // TODO:  check the error. It might just be that the dbchanell is already open
      }
    })

    // Make stuff available for the template
    return {
      // from self
      ...toRefs(state),
      myClientFields,
      onUpdateClientFields,
      onFormClosed,
      client,

      // from use ClienFlowApi
      hasResponseError,
      awaitingResponse,
      errorDisplay,

      // from useApiErrorViewHandler
      showingResponseError,
      showResponseError,
      toggleShowApiError,
      errorVisibilityHasChangedTo,
    }
  },
})

